/**
 * Equipment Crafting Recipes
 * Creates equipment items with fixed quality based on tier
 *
 * Quality: Tier 1 = 70%, Tier 2 = 80%, Tier 3 = 90%, Tier 4 = 100%, etc.
 *
 * Level Requirements:
 * - Free gear (no level req): Tier 1
 * - Level 5-15: Tier 1 (Common)
 * - Level 20-30: Tier 2 (Uncommon)
 * - Level 35-45: Tier 3 (Rare)
 * - Level 50-60: Tier 4 (Epic)
 * - Level 70+: Tier 5 (Legendary)
 *
 * Technology Requirements:
 * - Tier 1: None
 * - Tier 2: metallurgy (Tier 3 tech)
 * - Tier 3: blacksmithing (Tier 4 tech)
 * - Tier 4: steel_production (Tier 5 tech)
 * - Tier 5: eternal_materials (Tier 10 tech)
 *
 * NOTE: All costs are 5x base for meaningful investment
 * All itemIds reference actual equipment from src/data/dungeon/equipment.js
 */

export const EQUIPMENT_RECIPES = {
  // ===========================================
  // TIER 1 - Starter Equipment (70% quality)
  // Free gear - No level requirement, no tech requirement
  // ===========================================

  // --- WEAPONS ---
  craft_rusty_dagger: {
    id: 'craft_rusty_dagger',
    name: 'Rusty Dagger',
    icon: '🗡️',
    category: 'equipment',
    tier: 1,
    levelReq: 1,
    description: 'A worn dagger, barely sharp enough to cut. Perfect for beginners.',
    cost: {
      resources: { wood: 50, gold: 25 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'rustyDagger',
      quantity: 1,
    },
  },

  craft_wooden_club: {
    id: 'craft_wooden_club',
    name: 'Wooden Club',
    icon: '🏏',
    category: 'equipment',
    tier: 1,
    levelReq: 10,
    description: 'A simple club carved from hardwood. Better than nothing.',
    cost: {
      resources: { wood: 100, gold: 50 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'woodenClub',
      quantity: 1,
    },
  },

  craft_broken_sword: {
    id: 'craft_broken_sword',
    name: 'Broken Sword',
    icon: '⚔️',
    category: 'equipment',
    tier: 1,
    levelReq: 15,
    description: 'Half a blade is better than none.',
    cost: {
      resources: { iron: 75, gold: 60 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'brokenSword',
      quantity: 1,
    },
  },

  // --- ARMOR (TOPS) ---
  craft_leather_vest: {
    id: 'craft_leather_vest',
    name: 'Leather Vest',
    icon: '🧥',
    category: 'equipment',
    tier: 1,
    levelReq: 1,
    description: 'Basic protection from cuts and scrapes.',
    cost: {
      resources: { gold: 40 },
      materials: { leather: 15 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'leatherVest',
      quantity: 1,
    },
  },

  craft_cloth_tunic: {
    id: 'craft_cloth_tunic',
    name: 'Cloth Tunic',
    icon: '👕',
    category: 'equipment',
    tier: 1,
    levelReq: 10,
    description: 'Light and comfortable, but offers little defense.',
    cost: {
      resources: { gold: 75 },
      materials: { cloth: 25 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'clothTunic',
      quantity: 1,
    },
  },

  craft_padded_armor: {
    id: 'craft_padded_armor',
    name: 'Padded Armor',
    icon: '🦺',
    category: 'equipment',
    tier: 1,
    levelReq: 15,
    description: 'Quilted layers provide modest protection.',
    cost: {
      resources: { gold: 90 },
      materials: { cloth: 30, leather: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'paddedArmor',
      quantity: 1,
    },
  },

  // --- HELMETS ---
  craft_leather_cap: {
    id: 'craft_leather_cap',
    name: 'Leather Cap',
    icon: '🧢',
    category: 'equipment',
    tier: 1,
    levelReq: 1,
    description: 'Protects your head from light blows.',
    cost: {
      resources: { gold: 60 },
      materials: { leather: 15 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'leatherCap',
      quantity: 1,
    },
  },

  craft_iron_helmet: {
    id: 'craft_iron_helmet',
    name: 'Iron Helmet',
    icon: '⛑️',
    category: 'equipment',
    tier: 1,
    levelReq: 10,
    description: 'A basic iron helm, dented but functional.',
    cost: {
      resources: { iron: 80, gold: 50 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'ironHelmet',
      quantity: 1,
    },
  },

  craft_wooden_mask: {
    id: 'craft_wooden_mask',
    name: 'Wooden Mask',
    icon: '🎭',
    category: 'equipment',
    tier: 1,
    levelReq: 15,
    description: 'A ceremonial mask that offers minimal protection.',
    cost: {
      resources: { wood: 60, gold: 70 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'woodenMask',
      quantity: 1,
    },
  },

  // --- BOOTS ---
  craft_leather_boots: {
    id: 'craft_leather_boots',
    name: 'Leather Boots',
    icon: '👢',
    category: 'equipment',
    tier: 1,
    levelReq: 1,
    description: 'Simple boots for basic foot protection.',
    cost: {
      resources: { gold: 50 },
      materials: { leather: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'leatherBoots',
      quantity: 1,
    },
  },

  craft_wooden_sandals: {
    id: 'craft_wooden_sandals',
    name: 'Wooden Sandals',
    icon: '🩴',
    category: 'equipment',
    tier: 1,
    levelReq: 10,
    description: 'Light and airy, but prone to splinters.',
    cost: {
      resources: { wood: 40, gold: 30 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'woodenSandals',
      quantity: 1,
    },
  },

  craft_iron_sabatons: {
    id: 'craft_iron_sabatons',
    name: 'Iron Sabatons',
    icon: '👢',
    category: 'equipment',
    tier: 1,
    levelReq: 15,
    description: 'Heavy iron boots, slow but protective.',
    cost: {
      resources: { iron: 100, gold: 60 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'ironSabatons',
      quantity: 1,
    },
  },

  // --- OFFHAND ---
  craft_wooden_shield: {
    id: 'craft_wooden_shield',
    name: 'Wooden Shield',
    icon: '🛡️',
    category: 'equipment',
    tier: 1,
    levelReq: 10,
    description: 'A basic wooden shield, sturdy but worn.',
    cost: {
      resources: { wood: 80, gold: 40 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'woodenShield',
      quantity: 1,
    },
  },

  craft_broken_buckler: {
    id: 'craft_broken_buckler',
    name: 'Broken Buckler',
    icon: '🛡️',
    category: 'equipment',
    tier: 1,
    levelReq: 15,
    description: 'A small, damaged buckler that still offers some protection.',
    cost: {
      resources: { iron: 60, gold: 50 },
      materials: {},
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'brokenBuckler',
      quantity: 1,
    },
  },

  // ===========================================
  // TIER 2 - Iron/Steel Equipment (80% quality)
  // Level 20-30, Requires: metallurgy
  // ===========================================

  // --- WEAPONS ---
  craft_steel_sword: {
    id: 'craft_steel_sword',
    name: 'Steel Sword',
    icon: '⚔️',
    category: 'equipment',
    tier: 2,
    levelReq: 20,
    description: 'A well-crafted blade of tempered steel.',
    cost: {
      resources: { iron: 150, gold: 125 },
      materials: { bone: 25, leather: 15 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'steelSword',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_hunting_bow: {
    id: 'craft_hunting_bow',
    name: 'Hunting Bow',
    icon: '🏹',
    category: 'equipment',
    tier: 2,
    levelReq: 25,
    description: 'A reliable bow for hunting game... or enemies.',
    cost: {
      resources: { wood: 200, gold: 150 },
      materials: { leather: 30, cloth: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'huntingBow',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_war_hammer: {
    id: 'craft_war_hammer',
    name: 'War Hammer',
    icon: '🔨',
    category: 'equipment',
    tier: 2,
    levelReq: 30,
    description: 'Crushes armor and bones with equal ease.',
    cost: {
      resources: { iron: 200, gold: 175 },
      materials: { bone: 35, leather: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'warHammer',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  // --- ARMOR (TOPS) ---
  craft_chainmail: {
    id: 'craft_chainmail',
    name: 'Chainmail',
    icon: '🛡️',
    category: 'equipment',
    tier: 2,
    levelReq: 20,
    description: 'Interlocking metal rings protect vital areas.',
    cost: {
      resources: { iron: 200, gold: 150 },
      materials: { leather: 30 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'chainmail',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_scale_armor: {
    id: 'craft_scale_armor',
    name: 'Scale Armor',
    icon: '🐉',
    category: 'equipment',
    tier: 2,
    levelReq: 25,
    description: 'Armor made from overlapping metal scales.',
    cost: {
      resources: { iron: 225, gold: 175 },
      materials: { leather: 35 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'scaleArmor',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_studded_leather: {
    id: 'craft_studded_leather',
    name: 'Studded Leather',
    icon: '🧥',
    category: 'equipment',
    tier: 2,
    levelReq: 30,
    description: 'Reinforced leather with metal studs.',
    cost: {
      resources: { iron: 150, gold: 125 },
      materials: { leather: 50, cloth: 15 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'studdedLeather',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  // --- HELMETS ---
  craft_steel_helm: {
    id: 'craft_steel_helm',
    name: 'Steel Helm',
    icon: '⛑️',
    category: 'equipment',
    tier: 2,
    levelReq: 20,
    description: 'A well-crafted steel helmet with a visor.',
    cost: {
      resources: { iron: 125, gold: 100 },
      materials: { leather: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'steelHelm',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_knight_helmet: {
    id: 'craft_knight_helmet',
    name: 'Knight Helmet',
    icon: '👑',
    category: 'equipment',
    tier: 2,
    levelReq: 25,
    description: 'Full-face protection for the noble warrior.',
    cost: {
      resources: { iron: 150, gold: 125 },
      materials: { leather: 15, cloth: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'knightHelmet',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_arcane_hood: {
    id: 'craft_arcane_hood',
    name: 'Arcane Hood',
    icon: '🧙',
    category: 'equipment',
    tier: 2,
    levelReq: 30,
    description: 'A hood imbued with protective enchantments.',
    cost: {
      resources: { gold: 175 },
      materials: { cloth: 40, essence: 5 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'arcaneHood',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  // --- BOOTS ---
  craft_steel_boots: {
    id: 'craft_steel_boots',
    name: 'Steel Boots',
    icon: '👢',
    category: 'equipment',
    tier: 2,
    levelReq: 20,
    description: 'Well-balanced steel footwear.',
    cost: {
      resources: { iron: 125, gold: 100 },
      materials: { leather: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'steelBoots',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_swift_runners: {
    id: 'craft_swift_runners',
    name: 'Swift Runners',
    icon: '👟',
    category: 'equipment',
    tier: 2,
    levelReq: 25,
    description: 'Enchanted boots that increase your speed.',
    cost: {
      resources: { gold: 200 },
      materials: { leather: 35, cloth: 15, essence: 3 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'swiftRunners',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_war_boots: {
    id: 'craft_war_boots',
    name: 'War Boots',
    icon: '🥾',
    category: 'equipment',
    tier: 2,
    levelReq: 30,
    description: 'Reinforced boots for the battlefield.',
    cost: {
      resources: { iron: 150, gold: 125 },
      materials: { leather: 30 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'warBoots',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  // --- OFFHAND ---
  craft_steel_shield: {
    id: 'craft_steel_shield',
    name: 'Steel Shield',
    icon: '🛡️',
    category: 'equipment',
    tier: 2,
    levelReq: 25,
    description: 'A well-crafted steel shield.',
    cost: {
      resources: { iron: 150, gold: 120 },
      materials: { leather: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'steelShield',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  craft_iron_buckler: {
    id: 'craft_iron_buckler',
    name: 'Iron Buckler',
    icon: '🛡️',
    category: 'equipment',
    tier: 2,
    levelReq: 30,
    description: 'A solid iron buckler for quick parries.',
    cost: {
      resources: { iron: 175, gold: 140 },
      materials: { leather: 25 },
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'ironBuckler',
      quantity: 1,
    },
    requiresTech: 'metallurgy',
  },

  // ===========================================
  // TIER 3 - Rare Equipment (90% quality)
  // Level 35-45, Requires: blacksmithing
  // ===========================================

  // --- WEAPONS ---
  craft_flaming_sword: {
    id: 'craft_flaming_sword',
    name: 'Flaming Sword',
    icon: '🔥',
    category: 'equipment',
    tier: 3,
    levelReq: 35,
    description: 'A blade wreathed in eternal flames.',
    cost: {
      resources: { steel: 125, gold: 500 },
      materials: { darkSteel: 25, essence: 15, sulfur: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'flamingSword',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_crystal_bow: {
    id: 'craft_crystal_bow',
    name: 'Crystal Bow',
    icon: '🏹',
    category: 'equipment',
    tier: 3,
    levelReq: 40,
    description: 'Arrows fired from this bow never miss their mark.',
    cost: {
      resources: { steel: 100, gold: 450, gems: 25 },
      materials: { crystal: 15, essence: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'crystalBow',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_thunder_axe: {
    id: 'craft_thunder_axe',
    name: 'Thunder Axe',
    icon: '⚡',
    category: 'equipment',
    tier: 3,
    levelReq: 45,
    description: 'Each strike echoes with the sound of thunder.',
    cost: {
      resources: { steel: 150, gold: 600 },
      materials: { darkSteel: 30, essence: 20, orcTusk: 5 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'thunderAxe',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  // --- ARMOR (TOPS) ---
  craft_plate_armor: {
    id: 'craft_plate_armor',
    name: 'Plate Armor',
    icon: '🛡️',
    category: 'equipment',
    tier: 3,
    levelReq: 35,
    description: 'Full body protection of solid steel plates.',
    cost: {
      resources: { steel: 200, gold: 750 },
      materials: { darkSteel: 40, leather: 25 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'plateArmor',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_dragonscale_mail: {
    id: 'craft_dragonscale_mail',
    name: 'Dragonscale Mail',
    icon: '🐲',
    category: 'equipment',
    tier: 3,
    levelReq: 40,
    description: 'Armor crafted from actual dragon scales.',
    cost: {
      resources: { steel: 175, gold: 800 },
      materials: { dragonScale: 8, darkSteel: 30 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'dragonscale',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_enchanted_robes: {
    id: 'craft_enchanted_robes',
    name: 'Enchanted Robes',
    icon: '👘',
    category: 'equipment',
    tier: 3,
    levelReq: 45,
    description: 'Magically woven robes that deflect blows.',
    cost: {
      resources: { gold: 700, magic: 150 },
      materials: { cloth: 60, essence: 25, rune: 5 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'enchantedRobes',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  // --- HELMETS ---
  craft_dragon_helm: {
    id: 'craft_dragon_helm',
    name: 'Dragon Helm',
    icon: '🐲',
    category: 'equipment',
    tier: 3,
    levelReq: 35,
    description: 'Shaped like a dragon\'s head, fearsome and strong.',
    cost: {
      resources: { steel: 100, gold: 350 },
      materials: { darkSteel: 20, dragonScale: 3 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'dragonHelm',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_crown_of_thrones: {
    id: 'craft_crown_of_thrones',
    name: 'Crown of Thrones',
    icon: '👑',
    category: 'equipment',
    tier: 3,
    levelReq: 40,
    description: 'A crown worn by ancient warrior kings.',
    cost: {
      resources: { steel: 80, gold: 500, gems: 20 },
      materials: { darkSteel: 15, essence: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'crownOfThrones',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_crystal_circlet: {
    id: 'craft_crystal_circlet',
    name: 'Crystal Circlet',
    icon: '💎',
    category: 'equipment',
    tier: 3,
    levelReq: 45,
    description: 'A delicate circlet that shields the mind.',
    cost: {
      resources: { gold: 600, gems: 35 },
      materials: { crystal: 20, essence: 15 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'crystalCirclet',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  // --- BOOTS ---
  craft_dragonrider_boots: {
    id: 'craft_dragonrider_boots',
    name: 'Dragonrider Boots',
    icon: '🐉',
    category: 'equipment',
    tier: 3,
    levelReq: 35,
    description: 'Boots worn by legendary dragon riders.',
    cost: {
      resources: { steel: 80, gold: 300 },
      materials: { dragonScale: 4, leather: 30 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'dragonrider',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_shadowstep: {
    id: 'craft_shadowstep',
    name: 'Shadowstep',
    icon: '🌑',
    category: 'equipment',
    tier: 3,
    levelReq: 40,
    description: 'Move silently through the shadows.',
    cost: {
      resources: { steel: 60, gold: 400 },
      materials: { darkSteel: 20, essence: 15, ectoplasm: 5 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'shadowstep',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_plate_greaves: {
    id: 'craft_plate_greaves',
    name: 'Plate Greaves',
    icon: '👢',
    category: 'equipment',
    tier: 3,
    levelReq: 45,
    description: 'Full plate protection for your legs.',
    cost: {
      resources: { steel: 120, gold: 350 },
      materials: { darkSteel: 25, leather: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'plateGreaves',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  // --- OFFHAND ---
  craft_dragon_shield: {
    id: 'craft_dragon_shield',
    name: 'Dragon Shield',
    icon: '🐲',
    category: 'equipment',
    tier: 3,
    levelReq: 40,
    description: 'A shield emblazoned with a dragon emblem.',
    cost: {
      resources: { steel: 150, gold: 400 },
      materials: { darkSteel: 30, dragonScale: 5 },
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'dragonShield',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  craft_crystal_aegis: {
    id: 'craft_crystal_aegis',
    name: 'Crystal Aegis',
    icon: '💎',
    category: 'equipment',
    tier: 3,
    levelReq: 45,
    description: 'A magical shield made of living crystal.',
    cost: {
      resources: { steel: 100, gold: 500, gems: 30 },
      materials: { crystal: 15, essence: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'crystalAegis',
      quantity: 1,
    },
    requiresTech: 'blacksmithing',
  },

  // ===========================================
  // TIER 4 - Epic Equipment (100% quality)
  // Level 50-60, Requires: steel_production
  // ===========================================

  // --- WEAPONS ---
  craft_dragonslayer: {
    id: 'craft_dragonslayer',
    name: 'Dragonslayer Blade',
    icon: '🐉',
    category: 'equipment',
    tier: 4,
    levelReq: 50,
    description: 'Forged to slay dragons, feared by all creatures.',
    cost: {
      resources: { steel: 250, gold: 2500, gems: 50 },
      materials: { dragonScale: 15, dragonHeart: 5, soulShard: 25 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'dragonslayerBlade',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_void_trident: {
    id: 'craft_void_trident',
    name: 'Void Trident',
    icon: '🔱',
    category: 'equipment',
    tier: 4,
    levelReq: 55,
    description: 'A weapon from beyond reality itself.',
    cost: {
      resources: { steel: 200, gold: 2000, magic: 300 },
      materials: { voidFragment: 10, soulShard: 20, essence: 30 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'voidTrident',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_starfallen_bow: {
    id: 'craft_starfallen_bow',
    name: 'Starfallen Bow',
    icon: '🏹',
    category: 'equipment',
    tier: 4,
    levelReq: 60,
    description: 'Its arrows are fragments of fallen stars.',
    cost: {
      resources: { steel: 175, gold: 2200, gems: 60 },
      materials: { crystal: 25, cosmicFragment: 5, essence: 25 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'starfallenBow',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  // --- ARMOR (TOPS) ---
  craft_titanium_plate: {
    id: 'craft_titanium_plate',
    name: 'Titanium Plate',
    icon: '⚜️',
    category: 'equipment',
    tier: 4,
    levelReq: 50,
    description: 'Impossibly light yet unbreakably strong.',
    cost: {
      resources: { steel: 150, gold: 2000, gems: 25 },
      materials: { dragonScale: 10, soulShard: 15, darkSteel: 50 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'titaniumPlate',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_celestial_robes: {
    id: 'craft_celestial_robes',
    name: 'Celestial Robes',
    icon: '✨',
    category: 'equipment',
    tier: 4,
    levelReq: 55,
    description: 'Woven from stardust and moonlight.',
    cost: {
      resources: { gold: 2500, magic: 400, gems: 40 },
      materials: { cloth: 80, crystal: 20, essence: 35 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'celestialRobes',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_demonhide_armor: {
    id: 'craft_demonhide_armor',
    name: 'Demonhide Armor',
    icon: '😈',
    category: 'equipment',
    tier: 4,
    levelReq: 60,
    description: 'Tanned from the hide of a greater demon.',
    cost: {
      resources: { steel: 180, gold: 2200 },
      materials: { demonHorn: 8, soulShard: 20, thickHide: 30 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'demonhideArmor',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  // --- HELMETS ---
  craft_titan_helm: {
    id: 'craft_titan_helm',
    name: 'Titan Helm',
    icon: '⛑️',
    category: 'equipment',
    tier: 4,
    levelReq: 50,
    description: 'Forged by giants, sized for mortals.',
    cost: {
      resources: { steel: 120, gold: 1200, gems: 20 },
      materials: { soulShard: 10, darkSteel: 30 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'titanHelm',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_void_mask: {
    id: 'craft_void_mask',
    name: 'Void Mask',
    icon: '👹',
    category: 'equipment',
    tier: 4,
    levelReq: 55,
    description: 'Those who wear it see into the abyss.',
    cost: {
      resources: { steel: 100, gold: 1500, magic: 200 },
      materials: { voidFragment: 8, soulShard: 12, essence: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'voidMask',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_celestial_crown: {
    id: 'craft_celestial_crown',
    name: 'Celestial Crown',
    icon: '👑',
    category: 'equipment',
    tier: 4,
    levelReq: 60,
    description: 'A crown blessed by the stars above.',
    cost: {
      resources: { gold: 1800, gems: 50, magic: 250 },
      materials: { crystal: 20, essence: 25, cosmicFragment: 3 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'celestialCrown',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  // --- BOOTS ---
  craft_windwalker: {
    id: 'craft_windwalker',
    name: 'Windwalker Boots',
    icon: '💨',
    category: 'equipment',
    tier: 4,
    levelReq: 50,
    description: 'Walk on air itself with these mystical boots.',
    cost: {
      resources: { steel: 100, gold: 1000, magic: 200 },
      materials: { essence: 30, soulShard: 8 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'windwalker',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_titan_greaves: {
    id: 'craft_titan_greaves',
    name: 'Titan Greaves',
    icon: '⚡',
    category: 'equipment',
    tier: 4,
    levelReq: 55,
    description: 'Each step shakes the earth.',
    cost: {
      resources: { steel: 140, gold: 1400 },
      materials: { darkSteel: 40, soulShard: 15, ogreHeart: 3 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'titanGreaves',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_voidwalkers: {
    id: 'craft_voidwalkers',
    name: 'Voidwalkers',
    icon: '🌌',
    category: 'equipment',
    tier: 4,
    levelReq: 60,
    description: 'Step between dimensions with ease.',
    cost: {
      resources: { steel: 120, gold: 1600, magic: 250 },
      materials: { voidFragment: 10, essence: 25, soulShard: 12 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'voidwalkers',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  // --- OFFHAND ---
  craft_titan_shield: {
    id: 'craft_titan_shield',
    name: 'Titan Shield',
    icon: '⚜️',
    category: 'equipment',
    tier: 4,
    levelReq: 55,
    description: 'A massive shield forged by titans.',
    cost: {
      resources: { steel: 200, gold: 1500, gems: 30 },
      materials: { soulShard: 20, darkSteel: 40 },
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'titanShield',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  craft_void_buckler: {
    id: 'craft_void_buckler',
    name: 'Void Buckler',
    icon: '🌑',
    category: 'equipment',
    tier: 4,
    levelReq: 60,
    description: 'A buckler that absorbs attacks into the void.',
    cost: {
      resources: { steel: 150, gold: 1800, magic: 200 },
      materials: { voidFragment: 12, soulShard: 15, essence: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'voidBuckler',
      quantity: 1,
    },
    requiresTech: 'steel_production',
  },

  // ===========================================
  // TIER 5 - Legendary Equipment (110% quality)
  // Level 70+, Requires: eternal_materials
  // ===========================================

  // --- WEAPONS ---
  craft_soulreaver: {
    id: 'craft_soulreaver',
    name: 'Soulreaver',
    icon: '⚔️',
    category: 'equipment',
    tier: 5,
    levelReq: 65,
    description: 'A blade that devours the souls of the slain.',
    cost: {
      resources: { steel: 300, gold: 5000, magic: 800 },
      materials: { soulShard: 50, lichPhylactery: 5, eldritchHeart: 3 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'soulreaver',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_void_blade: {
    id: 'craft_void_blade',
    name: 'Void Blade',
    icon: '🌑',
    category: 'equipment',
    tier: 5,
    levelReq: 80,
    description: 'A blade forged from crystallized void energy.',
    cost: {
      resources: { steel: 300, gold: 5000, magic: 1000, crystals: 100 },
      materials: { voidEssence: 25, cosmicFragment: 15, divineEssence: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'voidBlade',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_infinity_staff: {
    id: 'craft_infinity_staff',
    name: 'Infinity Staff',
    icon: '✨',
    category: 'equipment',
    tier: 5,
    levelReq: 75,
    description: 'Channels the infinite power of creation itself.',
    cost: {
      resources: { gold: 6000, magic: 1500, crystals: 150 },
      materials: { divineEssence: 15, starMetal: 20, cosmicFragment: 10 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'infinityStaff',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_eternity_bow: {
    id: 'craft_eternity_bow',
    name: 'Eternity Bow',
    icon: '🏹',
    category: 'equipment',
    tier: 5,
    levelReq: 90,
    description: 'Its arrows pierce through time itself.',
    cost: {
      resources: { steel: 250, gold: 5500, gems: 100 },
      materials: { starMetal: 25, cosmicFragment: 20, divineEssence: 12 },
    },
    output: {
      type: 'equipment',
      slot: 'weapon',
      itemId: 'eternityBow',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  // --- ARMOR (TOPS) ---
  craft_eternal_guardian: {
    id: 'craft_eternal_guardian',
    name: 'Eternal Guardian',
    icon: '🛡️',
    category: 'equipment',
    tier: 5,
    levelReq: 70,
    description: 'Armor that has protected heroes across the ages.',
    cost: {
      resources: { steel: 250, gold: 4000, magic: 750, crystals: 75 },
      materials: { divineEssence: 20, starMetal: 25, dragonScale: 15 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'eternalGuardian',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_void_plate: {
    id: 'craft_void_plate',
    name: 'Void Plate',
    icon: '🌌',
    category: 'equipment',
    tier: 5,
    levelReq: 80,
    description: 'Forged in the space between realities.',
    cost: {
      resources: { steel: 280, gold: 4500, magic: 900 },
      materials: { voidEssence: 25, cosmicFragment: 15, soulShard: 40 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'voidPlate',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_divine_mail: {
    id: 'craft_divine_mail',
    name: 'Divine Mail',
    icon: '⚜️',
    category: 'equipment',
    tier: 5,
    levelReq: 90,
    description: 'A gift from the gods themselves.',
    cost: {
      resources: { steel: 300, gold: 5500, magic: 1200, crystals: 100 },
      materials: { divineEssence: 30, starMetal: 30, eterniumIngot: 5 },
    },
    output: {
      type: 'equipment',
      slot: 'top',
      itemId: 'divineMail',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  // --- HELMETS ---
  craft_helm_of_eternity: {
    id: 'craft_helm_of_eternity',
    name: 'Helm of Eternity',
    icon: '⛑️',
    category: 'equipment',
    tier: 5,
    levelReq: 70,
    description: 'Worn by immortal heroes throughout time.',
    cost: {
      resources: { steel: 180, gold: 3000, magic: 400, crystals: 40 },
      materials: { divineEssence: 12, starMetal: 15 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'helmOfEternity',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_crown_of_gods: {
    id: 'craft_crown_of_gods',
    name: 'Crown of Gods',
    icon: '👑',
    category: 'equipment',
    tier: 5,
    levelReq: 80,
    description: 'Only the truly divine may wear this crown.',
    cost: {
      resources: { gold: 4500, gems: 100, magic: 600, crystals: 60 },
      materials: { divineEssence: 18, cosmicFragment: 12, starMetal: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'crownOfGods',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_infinity_mask: {
    id: 'craft_infinity_mask',
    name: 'Infinity Mask',
    icon: '💎',
    category: 'equipment',
    tier: 5,
    levelReq: 90,
    description: 'See all possible futures and pasts.',
    cost: {
      resources: { gold: 5000, gems: 150, magic: 800, crystals: 80 },
      materials: { divineEssence: 25, voidEssence: 15, eterniumIngot: 3 },
    },
    output: {
      type: 'equipment',
      slot: 'helmet',
      itemId: 'infinityMask',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  // --- BOOTS ---
  craft_mercury_wings: {
    id: 'craft_mercury_wings',
    name: 'Mercury Wings',
    icon: '👢',
    category: 'equipment',
    tier: 5,
    levelReq: 70,
    description: 'Wings on your heels grant godlike speed.',
    cost: {
      resources: { steel: 150, gold: 2500, magic: 600, crystals: 35 },
      materials: { divineEssence: 10, starMetal: 12 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'mercuryWings',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_infinity_stride: {
    id: 'craft_infinity_stride',
    name: 'Infinity Stride',
    icon: '✨',
    category: 'equipment',
    tier: 5,
    levelReq: 80,
    description: 'Walk across universes in a single step.',
    cost: {
      resources: { steel: 180, gold: 3500, magic: 800, crystals: 50 },
      materials: { divineEssence: 15, cosmicFragment: 10, starMetal: 18 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'infinityStride',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_eternalsole: {
    id: 'craft_eternalsole',
    name: 'Eternalsole',
    icon: '🌟',
    category: 'equipment',
    tier: 5,
    levelReq: 90,
    description: 'These boots will never wear out.',
    cost: {
      resources: { steel: 200, gold: 4000, magic: 1000, crystals: 65 },
      materials: { divineEssence: 20, eterniumIngot: 4, starMetal: 22 },
    },
    output: {
      type: 'equipment',
      slot: 'boots',
      itemId: 'eternalsole',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  // --- OFFHAND ---
  craft_aegis_eternal: {
    id: 'craft_aegis_eternal',
    name: 'Aegis Eternal',
    icon: '🛡️',
    category: 'equipment',
    tier: 5,
    levelReq: 75,
    description: 'An unbreakable shield blessed by the gods.',
    cost: {
      resources: { steel: 200, gold: 3500, magic: 500, crystals: 50 },
      materials: { divineEssence: 15, starMetal: 20 },
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'aegisEternal',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },

  craft_cosmic_barrier: {
    id: 'craft_cosmic_barrier',
    name: 'Cosmic Barrier',
    icon: '🌌',
    category: 'equipment',
    tier: 5,
    levelReq: 85,
    description: 'A barrier woven from the fabric of space itself.',
    cost: {
      resources: { steel: 220, gold: 4500, magic: 750, crystals: 70 },
      materials: { cosmicFragment: 18, voidEssence: 15, starMetal: 25 },
    },
    output: {
      type: 'equipment',
      slot: 'offhand',
      itemId: 'cosmicBarrier',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },
};

export default EQUIPMENT_RECIPES;
